/**
 * FoxSightCone — accessible visualiser for the Glass-Jaw Fox's sight range.
 *
 * Ships the "Option E hybrid" treatment spec'd in
 * `docs/specs/SPEC-fox-sight-cone.md`:
 *
 *   1. A faint, always-on ground "Danger Stripe" disc-slice parented to the
 *      fox. The footprint always reads at a glance so the player can learn
 *      the fox's vision zone on their first approach without having to die
 *      to it. Rendered as a flat disc rotated onto Z = 0.
 *
 *   2. A volumetric translucent cone that ramps up ONLY while Minna's Aura
 *      Sight ability is active. Aura Sight is the canonical perception
 *      upgrade — when it's on, the tactical view genuinely improves.
 *
 * Four states are mapped to the colourblind-safe luminance ramp from the
 * spec so the cone is readable in deuteranopia/protanopia as well as in
 * pure grayscale:
 *
 *   - `patrol`              — pale brass   (#c49b3c, Y=0.55), thin wedge
 *   - `alerted`             — warm amber   (#d68a2c, Y=0.45), wider + pulsing
 *   - `hunting`             — copper-red   (#b04820, Y=0.30), narrow + long
 *   - `stealth-protected`   — cone visibly *fails to bite* the cat when she
 *                             is inside the footprint and Shadow Slip is
 *                             active. We darken + dim the materials and
 *                             shrink the volumetric mesh toward the fox so
 *                             it reads as "the cone can't reach her." Per
 *                             spec, this is the cleverest teaching moment —
 *                             the player sees `canDetect(..., isStealthed=
 *                             true) → false` instead of learning it through
 *                             failure.
 *
 * The visualiser never changes gameplay-critical detection semantics; it
 * only subscribes to existing `ABILITY_ACTIVATED` / `ABILITY_DEACTIVATED`
 * events to flip the volumetric overlay globally when Aura Sight toggles.
 * The per-fox state is driven by the owning Fox's `update()` loop.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";

import { NPC_TUNING } from "../../config/GameConfig";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";

/** Canonical visualiser states — see class docblock. */
export type FoxSightConeState =
  | "patrol"
  | "alerted"
  | "hunting"
  | "stealth-protected";

/** Hex strings from the colourblind-safe luminance ramp in the spec. */
export const SIGHT_CONE_COLORS = {
  patrol: "#c49b3c", // brass, Y ≈ 0.55
  alerted: "#d68a2c", // amber, Y ≈ 0.45
  hunting: "#b04820", // copper-red, Y ≈ 0.30
  stealth: "#5a6a75", // cold desaturated — "the cone goes blind"
} as const;

/** Per-fox tuning knobs passed into the FoxSightCone constructor. */
export interface FoxSightConeConfig {
  /** Half-angle (radians) of the cone while patrolling. Default ~55°. */
  patrolHalfAngle?: number;
  /** Half-angle while alerted. Default ~80°. */
  alertHalfAngle?: number;
  /** Half-angle while hunting. Default ~20°. */
  huntHalfAngle?: number;
  /** Cone length in world units. Default = NPC_TUNING.FOX.ALERT_RADIUS. */
  radius?: number;
  /** Fox eye height above its mesh origin — where the volumetric cone
   *  originates from. Default 0.4 (matches the placeholder fox head). */
  eyeHeight?: number;
  /** Optional solid colour override for the cone (e.g. L3 starlit-blue). */
  colorOverride?: Color3;
}

/** Per-frame inputs to `setState()`. */
export interface FoxSightConeFrame {
  /** Canonical state driven by the owning Fox's state machine. */
  state: FoxSightConeState;
  /** Is Minna's Aura Sight ability active right now? Drives the volumetric
   *  overlay's alpha ramp. Defaults to the class-level subscription state. */
  auraActive?: boolean;
  /** World position of a stealthed target (usually Minna). When provided and
   *  the target is inside the cone footprint, the cone visibly dims /
   *  shrinks so it "fails to bite." */
  stealthedTargetPos?: Vector3;
  /** Seconds since level start — used for pulse / shimmer timing. */
  time: number;
}

/** Default tuning knobs. */
const DEFAULTS = {
  patrolHalfAngle: Math.PI * 0.3, // ~54°
  alertHalfAngle: Math.PI * 0.44, // ~79°
  huntHalfAngle: Math.PI * 0.11, // ~20°
  radius: NPC_TUNING.FOX.ALERT_RADIUS,
  eyeHeight: 0.4,
};

/**
 * Track the globally-active state of Minna's Aura Sight via the event bus.
 * The volumetric overlay is a Minna-perception upgrade, so toggling it at
 * the event layer means every fox in the scene reacts simultaneously with
 * no per-fox wiring required at the level layer.
 */
let globalAuraSightActive = false;
let globalSubscribersBound = 0;
let globalAuraActivateUnsub: (() => void) | null = null;
let globalAuraDeactivateUnsub: (() => void) | null = null;

function bindGlobalAuraSubscription(): void {
  globalSubscribersBound++;
  if (globalSubscribersBound > 1) return;

  globalAuraActivateUnsub = gameEvents.on(
    GameEvents.ABILITY_ACTIVATED,
    (data) => {
      if (data?.abilityId === "auraSight") globalAuraSightActive = true;
    },
  );
  globalAuraDeactivateUnsub = gameEvents.on(
    GameEvents.ABILITY_DEACTIVATED,
    (data) => {
      if (data?.abilityId === "auraSight") globalAuraSightActive = false;
    },
  );
}

function unbindGlobalAuraSubscription(): void {
  globalSubscribersBound = Math.max(0, globalSubscribersBound - 1);
  if (globalSubscribersBound === 0) {
    globalAuraActivateUnsub?.();
    globalAuraDeactivateUnsub?.();
    globalAuraActivateUnsub = null;
    globalAuraDeactivateUnsub = null;
    globalAuraSightActive = false;
  }
}

/**
 * Internal test hook — lets unit tests read and reset the module-level Aura
 * Sight flag without tripping over the persistent EventBus. Not part of
 * the public API consumed by gameplay code.
 */
export const __foxSightConeInternals = {
  isAuraActive(): boolean {
    return globalAuraSightActive;
  },
  resetForTests(): void {
    globalAuraSightActive = false;
    globalAuraActivateUnsub?.();
    globalAuraDeactivateUnsub?.();
    globalAuraActivateUnsub = null;
    globalAuraDeactivateUnsub = null;
    globalSubscribersBound = 0;
  },
};

/**
 * One FoxSightCone per Fox. Owns its ground disc, volumetric cone mesh,
 * and both materials. The Fox wires `setState()` into its update loop.
 */
export class FoxSightCone {
  private readonly scene: Scene;
  private readonly anchor: AbstractMesh;
  private readonly cfg: Required<FoxSightConeConfig>;

  /** Flat disc on y ≈ 0 — the always-on "Danger Stripe." */
  private groundDisc: Mesh;
  /** Volumetric cone, visible only when Aura Sight is active. */
  private volumeCone: Mesh;

  private groundMat: StandardMaterial;
  private volumeMat: StandardMaterial;

  /** Current target alpha for the volumetric mesh — lerped per frame so the
   *  ability toggle doesn't snap. */
  private targetVolumeAlpha = 0;
  private currentVolumeAlpha = 0;
  private disposed = false;

  constructor(scene: Scene, anchor: AbstractMesh, config: FoxSightConeConfig = {}) {
    this.scene = scene;
    this.anchor = anchor;
    this.cfg = {
      patrolHalfAngle: config.patrolHalfAngle ?? DEFAULTS.patrolHalfAngle,
      alertHalfAngle: config.alertHalfAngle ?? DEFAULTS.alertHalfAngle,
      huntHalfAngle: config.huntHalfAngle ?? DEFAULTS.huntHalfAngle,
      radius: config.radius ?? DEFAULTS.radius,
      eyeHeight: config.eyeHeight ?? DEFAULTS.eyeHeight,
      colorOverride: config.colorOverride ?? new Color3(0, 0, 0),
    };

    // ── Always-on ground disc ──
    // CreateDisc with `arc` as fraction-of-circle so the wedge footprint
    // is symmetric around the fox's forward vector. Parented to the fox
    // mesh, so it rotates for free with the NPC's facing direction.
    this.groundDisc = MeshBuilder.CreateDisc(
      `foxSightCone_${anchor.name}_ground`,
      {
        radius: this.cfg.radius,
        tessellation: 24,
        arc: (this.cfg.alertHalfAngle * 2) / (Math.PI * 2),
      },
      scene,
    );
    this.groundDisc.parent = anchor;
    this.groundDisc.rotation.x = Math.PI / 2;
    this.groundDisc.position.y = -0.45; // just below the fox's feet
    // Center the wedge on +X (the fox's facing direction per `NPC.ts:178`).
    this.groundDisc.rotation.z = -this.cfg.alertHalfAngle;
    this.groundDisc.isPickable = false;

    this.groundMat = new StandardMaterial(
      `foxSightCone_${anchor.name}_groundMat`,
      scene,
    );
    this.groundMat.disableLighting = true;
    this.groundMat.backFaceCulling = false;
    this.groundMat.specularColor = Color3.Black();
    this.groundMat.diffuseColor = Color3.FromHexString(SIGHT_CONE_COLORS.patrol);
    this.groundMat.emissiveColor = Color3.FromHexString(SIGHT_CONE_COLORS.patrol);
    this.groundMat.alpha = 0.14;
    this.groundDisc.material = this.groundMat;

    // ── Volumetric cone (Aura Sight-only) ──
    // CreateCylinder with topRadius=0 gives us a cone. We orient it so
    // its long axis points along +X (the fox's facing direction). Base
    // diameter is tuned for the hunting half-angle so the narrowest
    // state still has visible width; the ground disc carries the true
    // wedge footprint so this never has to be pixel-perfect.
    this.volumeCone = MeshBuilder.CreateCylinder(
      `foxSightCone_${anchor.name}_volume`,
      {
        height: this.cfg.radius,
        diameterTop: 0,
        diameterBottom: Math.max(
          0.6,
          2 * this.cfg.radius * Math.sin(this.cfg.alertHalfAngle),
        ),
        tessellation: 18,
      },
      scene,
    );
    this.volumeCone.parent = anchor;
    // Rotate so the cone apex is at the fox's eye and the cone opens
    // forward along +X (local to the fox).
    this.volumeCone.rotation.z = -Math.PI / 2;
    this.volumeCone.position.x = this.cfg.radius / 2;
    this.volumeCone.position.y = this.cfg.eyeHeight;
    this.volumeCone.isPickable = false;

    this.volumeMat = new StandardMaterial(
      `foxSightCone_${anchor.name}_volumeMat`,
      scene,
    );
    this.volumeMat.disableLighting = true;
    this.volumeMat.backFaceCulling = false;
    this.volumeMat.specularColor = Color3.Black();
    this.volumeMat.diffuseColor = Color3.FromHexString(SIGHT_CONE_COLORS.patrol);
    this.volumeMat.emissiveColor = Color3.FromHexString(SIGHT_CONE_COLORS.patrol);
    this.volumeMat.alpha = 0;
    this.volumeCone.material = this.volumeMat;
    this.volumeCone.visibility = 0; // start hidden — Aura Sight off
    // Render the volumetric cone "behind" the NPC meshes so a stealthed
    // cat inside the footprint remains clearly silhouetted. That's the
    // visual payoff for the stealth-protected teaching moment.
    this.volumeCone.renderingGroupId = 0;

    bindGlobalAuraSubscription();
  }

  /**
   * Per-frame driver called by the owning Fox. Drives every material
   * uniform and the volumetric cone's alpha ramp.
   */
  setState(frame: FoxSightConeFrame): void {
    if (this.disposed) return;

    const auraActive = frame.auraActive ?? globalAuraSightActive;
    const baseColor = this.resolveStateColor(frame.state);
    const stealthBite = this.evaluateStealthBite(frame);

    // ── Ground disc ──
    const groundAlpha = this.groundAlphaFor(frame.state, frame.time);
    const groundColor = stealthBite.active
      ? this.dimColor(baseColor, stealthBite.intensity)
      : baseColor;

    this.groundMat.diffuseColor = groundColor;
    this.groundMat.emissiveColor = groundColor;
    this.groundMat.alpha = groundAlpha * (1 - stealthBite.intensity * 0.75);

    // Re-scale the ground disc to match the active half-angle. CreateDisc
    // bakes the arc at creation, so instead of rebuilding the mesh we
    // visually approximate the narrower hunting cone by uniformly
    // shrinking the mesh scale — the behaviour radius is still authored
    // on the Fox itself, so this is pure visual approximation.
    const halfAngleScale = this.halfAngleFor(frame.state) / this.cfg.alertHalfAngle;
    this.groundDisc.scaling.x = halfAngleScale;
    this.groundDisc.scaling.y = halfAngleScale;

    // ── Volumetric cone (Aura Sight gated) ──
    this.targetVolumeAlpha = auraActive
      ? this.volumeAlphaFor(frame.state, frame.time)
      : 0;

    // Lerp towards the target alpha (~220ms visual ramp) so toggling the
    // ability doesn't snap in.
    const lerpRate = Math.min(1, 0.18);
    this.currentVolumeAlpha +=
      (this.targetVolumeAlpha - this.currentVolumeAlpha) * lerpRate;

    const volumeColor = stealthBite.active
      ? this.dimColor(baseColor, stealthBite.intensity)
      : baseColor;
    this.volumeMat.diffuseColor = volumeColor;
    this.volumeMat.emissiveColor = volumeColor;
    this.volumeMat.alpha = this.currentVolumeAlpha * (1 - stealthBite.intensity * 0.85);
    this.volumeCone.visibility = this.currentVolumeAlpha > 0.001 ? 1 : 0;
    this.volumeCone.scaling.x = halfAngleScale;
    this.volumeCone.scaling.z = halfAngleScale;

    // Stealth-protected "bite failure" payoff: visibly shrink the cone
    // back toward the fox when a stealthed cat is inside it. Player sees
    // the cone try and fail to reach her.
    if (stealthBite.active) {
      const bite = 1 - stealthBite.intensity * 0.4;
      this.volumeCone.scaling.y = bite;
    } else {
      this.volumeCone.scaling.y = 1;
    }
  }

  /** Read the ground disc mesh (tests + scene code). */
  get groundMesh(): Mesh {
    return this.groundDisc;
  }

  /** Read the volumetric cone mesh (tests + scene code). */
  get volumeMesh(): Mesh {
    return this.volumeCone;
  }

  /** Current colourblind-safe hex for introspection in tests. */
  get currentColorHex(): string {
    return "#" + this.groundMat.diffuseColor
      .toHexString()
      .replace(/^#/, "")
      .toLowerCase();
  }

  /** True if the volumetric cone is currently rendering. */
  get isVolumeVisible(): boolean {
    return this.currentVolumeAlpha > 0.001;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    this.groundDisc.dispose();
    this.volumeCone.dispose();
    this.groundMat.dispose();
    this.volumeMat.dispose();

    unbindGlobalAuraSubscription();
  }

  // ── internals ───────────────────────────────────────────────────────

  private resolveStateColor(state: FoxSightConeState): Color3 {
    if (this.cfg.colorOverride.r + this.cfg.colorOverride.g + this.cfg.colorOverride.b > 0) {
      return this.cfg.colorOverride;
    }
    switch (state) {
      case "patrol":
        return Color3.FromHexString(SIGHT_CONE_COLORS.patrol);
      case "alerted":
        return Color3.FromHexString(SIGHT_CONE_COLORS.alerted);
      case "hunting":
        return Color3.FromHexString(SIGHT_CONE_COLORS.hunting);
      case "stealth-protected":
        return Color3.FromHexString(SIGHT_CONE_COLORS.stealth);
    }
  }

  private halfAngleFor(state: FoxSightConeState): number {
    switch (state) {
      case "patrol":
        return this.cfg.patrolHalfAngle;
      case "alerted":
        return this.cfg.alertHalfAngle;
      case "hunting":
        return this.cfg.huntHalfAngle;
      case "stealth-protected":
        // While stealth-protected, retain whatever the caller's state
        // *was* visually — falling back to the patrol angle keeps the
        // cone from collapsing to a point on top of Minna, which is the
        // reading the spec explicitly warns against ("cone visibly FAILS
        // to bite" — needs to still be a cone to read as failure).
        return this.cfg.patrolHalfAngle;
    }
  }

  /** Per-state ground-disc alpha with slow pulse. */
  private groundAlphaFor(state: FoxSightConeState, time: number): number {
    switch (state) {
      case "patrol":
        return 0.12 + Math.sin(time * 0.5 * Math.PI * 2) * 0.02;
      case "alerted":
        return 0.25 + Math.sin(time * 1.5 * Math.PI * 2) * 0.06;
      case "hunting":
        return 0.35 + Math.sin(time * 3 * Math.PI * 2) * 0.08;
      case "stealth-protected":
        return 0.1;
    }
  }

  /** Per-state volumetric-cone alpha (only used while Aura Sight is on). */
  private volumeAlphaFor(state: FoxSightConeState, time: number): number {
    switch (state) {
      case "patrol":
        return 0.25 + Math.sin(time * 0.5 * Math.PI * 2) * 0.04;
      case "alerted":
        return 0.45 + Math.sin(time * 1.5 * Math.PI * 2) * 0.06;
      case "hunting":
        return 0.55 + Math.sin(time * 3 * Math.PI * 2) * 0.08;
      case "stealth-protected":
        return 0.18;
    }
  }

  /**
   * Decide whether a stealthed cat should "break" the cone — i.e. we
   * render the stealth-protected failure beat. Active when the caller
   * both (a) supplied a target position and (b) the target is inside
   * the cone's footprint (forward-hemisphere + within radius).
   *
   * Returns an `intensity` in [0, 1] that ramps up the deeper the cat is
   * inside the cone — the further in she walks, the more visibly the
   * cone fails, which matches the spec's "cone passes through / dims
   * where the cat stands" direction.
   */
  private evaluateStealthBite(frame: FoxSightConeFrame): {
    active: boolean;
    intensity: number;
  } {
    if (!frame.stealthedTargetPos || frame.state === "stealth-protected") {
      // If caller set state: "stealth-protected" explicitly, treat the
      // whole cone as failing. Maximum intensity.
      if (frame.state === "stealth-protected") {
        return { active: true, intensity: 1 };
      }
      return { active: false, intensity: 0 };
    }

    const foxPos = this.anchor.getAbsolutePosition();
    const dx = frame.stealthedTargetPos.x - foxPos.x;
    const dy = frame.stealthedTargetPos.y - foxPos.y;
    const distSq = dx * dx + dy * dy;
    if (distSq > this.cfg.radius * this.cfg.radius) {
      return { active: false, intensity: 0 };
    }

    // Facing check — the fox mesh's rotation.y is either 0 or π per
    // NPC.ts. Read it and decide whether the target is in the forward
    // hemisphere.
    const facingY = (this.anchor as unknown as { rotation: Vector3 }).rotation.y;
    const forwardX = Math.cos(facingY);
    const forwardY = 0; // fox faces +X, not up/down
    void forwardY;
    // Dot product of (forward, target-offset) in XY; only care about sign+magnitude.
    const forwardDot = dx * forwardX;
    if (forwardDot < 0) {
      // Target is behind the fox — not inside the visible cone.
      return { active: false, intensity: 0 };
    }

    // Intensity ramps up as the cat gets closer to the fox (deep inside
    // the cone). At the very edge, intensity ~= 0.35; at point-blank,
    // intensity saturates to 1.0.
    const dist = Math.sqrt(distSq);
    const proximity = 1 - dist / this.cfg.radius;
    const intensity = Math.min(1, Math.max(0.35, proximity));
    return { active: true, intensity };
  }

  /** Blend towards a cool desaturated tone as the cone "fails". */
  private dimColor(base: Color3, intensity: number): Color3 {
    const dim = Color3.FromHexString(SIGHT_CONE_COLORS.stealth);
    return new Color3(
      base.r * (1 - intensity) + dim.r * intensity,
      base.g * (1 - intensity) + dim.g * intensity,
      base.b * (1 - intensity) + dim.b * intensity,
    );
  }
}
